import { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Clear as ClearIcon } from "@mui/icons-material";
import { groupSchoolsForMenu } from "../../../utils/schoolCategoryFilter";

/**
 * Multi-select checkbox dropdown for filtering by school (schools.id).
 * Supports search within the menu and clear. Empty selection = all schools.
 *
 * Schools follow the master-form categories: Independent, then Grammar, then Both.
 * Within each category, consortium schools come before non-consortium schools.
 */
const TargetSchoolFilterSelect = ({
  schools = [],
  value = [],
  onChange,
  size = "small",
  sx,
  labelId = "target-school-filter-label",
  targetSchools = [],
  category = "",
}) => {
  const selected = Array.isArray(value) ? value.map(String) : [];
  const myTargets = useMemo(
    () => new Set((Array.isArray(targetSchools) ? targetSchools : []).map((s) => String(s.id))),
    [targetSchools]
  );
  const [schoolSearch, setSchoolSearch] = useState("");
  const [open, setOpen] = useState(false);

  // The options list only holds schools that have content, so a target school with nothing
  // published would otherwise be missing entirely — merge those in, flagged as empty.
  const options = useMemo(() => {
    const known = new Set(schools.map((s) => String(s.id)));
    const missing = (Array.isArray(targetSchools) ? targetSchools : [])
      .filter((s) => s?.id != null && !known.has(String(s.id)))
      .map((s) => ({ ...s, unavailable: true }));
    return [...schools, ...missing];
  }, [schools, targetSchools]);

  const groups = useMemo(() => {
    const q = schoolSearch.trim().toLowerCase();
    const list = q
      ? options.filter((s) => String(s.name || "").toLowerCase().includes(q))
      : options;
    return groupSchoolsForMenu(list, category);
  }, [options, schoolSearch, category]);

  const matchCount = groups.reduce((sum, group) => sum + group.schools.length, 0);
  const showHeaders = groups.length >= 1;

  const handleChange = (e) => {
    const next = Array.isArray(e.target.value) ? e.target.value.map(String) : [];
    // Sentinel "" from "All schools" / clear means empty selection.
    if (next.includes("")) {
      onChange?.([]);
      return;
    }
    onChange?.(next.filter(Boolean));
  };

  const handleClear = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    onChange?.([]);
    setSchoolSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSchoolSearch("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flex: { xs: "1 1 100%", sm: "0 0 auto" },
        minWidth: { sm: 200 },
        maxWidth: { xs: "100%", sm: 320 },
        ...sx,
      }}
    >
      <FormControl size={size} sx={{ flex: 1, minWidth: 0 }}>
        <InputLabel id={labelId}>School</InputLabel>
        <Select
          labelId={labelId}
          multiple
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          value={selected}
          label="School"
          onChange={handleChange}
          renderValue={(vals) => {
            if (!vals || vals.length === 0) return "All schools";
            const names = vals
              .map((id) => options.find((s) => String(s.id) === String(id))?.name)
              .filter(Boolean);
            if (names.length <= 2) return names.join(", ");
            return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              sx: { maxHeight: 400 },
            },
          }}
        >
          <ListSubheader sx={{ bgcolor: "background.paper", lineHeight: "normal", py: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search schools..."
              value={schoolSearch}
              onChange={(e) => setSchoolSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: schoolSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSchoolSearch("");
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </ListSubheader>
          <MenuItem value="">
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox size="small" checked={selected.length === 0} />
            </ListItemIcon>
            <ListItemText primary="All schools" />
          </MenuItem>
          {matchCount === 0 ? (
            <MenuItem disabled>
              <ListItemText primary="No schools match" />
            </MenuItem>
          ) : (
            // flatMap into ONE flat array, never fragments: Select reads props.value off
            // its direct children, so wrapping a group in <>…</> silently stops those
            // options selecting. A valueless ListSubheader is skipped by keyboard nav.
            groups.flatMap((group) => [
              showHeaders ? (
                <ListSubheader
                  key={group.label}
                  disableSticky
                  sx={{
                    bgcolor: "background.paper",
                    lineHeight: "2rem",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: "text.secondary",
                  }}
                >
                  {group.label}
                </ListSubheader>
              ) : null,
              ...group.schools.map((s) => {
                const sid = String(s.id);
                return (
                  <MenuItem key={s.id} value={sid} disabled={!!s.unavailable}>
                    <Checkbox size="small" checked={selected.includes(sid)} />
                    <ListItemText
                      primary={s.name}
                      secondary={
                        s.unavailable
                          ? "Nothing available yet"
                          : myTargets.has(sid)
                            ? "Your target school"
                            : null
                      }
                      secondaryTypographyProps={{ fontSize: "0.7rem" }}
                    />
                  </MenuItem>
                );
              }),
            ].filter(Boolean))
          )}
        </Select>
      </FormControl>
      {selected.length > 0 && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleClear}
          sx={{ textTransform: "none", flexShrink: 0, whiteSpace: "nowrap" }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

export default TargetSchoolFilterSelect;

/** Match if any selected school id equals school_id or is in target_school_ids. Empty = match all. */
export const matchesTargetSchools = (item, selectedIds) => {
  if (!selectedIds || selectedIds.length === 0) return true;
  const selected = selectedIds.map(String);
  const schoolId = item?.school_id != null ? String(item.school_id) : null;
  if (schoolId && selected.includes(schoolId)) return true;
  const targets = Array.isArray(item?.target_school_ids)
    ? item.target_school_ids.map(String)
    : [];
  return targets.some((id) => selected.includes(id));
};
